import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, Switch, TextInput, Modal, Linking,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { FontAwesome5 } from '@expo/vector-icons';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { useAlertModal } from '../../../common/hooks';
import {
  getLLCPublicKey,
  createLLCSetupIntent,
  saveLLCBillingCard,
  getTenantBillingStatus,
  toggleBillingEnabled,
  getUnbilledOrders,
  tenantTriggerCharge,
  triggerManualCharge,
  getBillingOverview,
  getOrderDetail,
  updateBillingSettings,
  getCronLogs as getCronLogsAPI,
  getTenantBrands,
  updateTenantBrandFees,
} from '../../../api/LLCBilling';
import BasicLayout from '../../../common/components/CustomLayout/BasicLayout';
import { renderBOHTlbDatePicker } from '../../../common/components/bohtoolbar';
import Select from '../../../common/components/ui/Select';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CYCLE_OPTIONS = ['daily', 'minutes', 'per_order'];
const STATUS_COLORS: Record<string, string> = {
  PAID: '#28A745',
  FAILED: '#DC3545',
  PENDING: '#FFC107',
  UNBILLED: '#6c757d',
  INVOICED: '#17a2b8',
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#222',
      fontFamily: 'sans-serif',
      '::placeholder': { color: '#aaa' },
    },
    invalid: { color: '#DC3545' },
  },
};

// ─── Card Setup Form (uses @stripe/react-stripe-js web SDK) ──────────────────

const CardSetupForm = ({ onCardSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { showAlert } = useAlertModal();
  const [isSaving, setIsSaving] = useState(false);
  const [tenantEmail, setTenantEmail] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleSaveCard = async () => {
    if (!stripe || !elements) return;
    // Validate email if provided
    if (tenantEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setIsSaving(true);
    try {
      // 1. Get SetupIntent client_secret from our backend
      let clientSecret: string | null = null;
      await createLLCSetupIntent({ tenant_email: tenantEmail, tenant_name: tenantName }, (res: any, status: number) => {
        if (status === 200) clientSecret = res.client_secret;
        else throw new Error(res?.error || 'Failed to create setup intent');
      });
      if (!clientSecret) throw new Error('No client_secret returned from server');

      // 2. Confirm the SetupIntent with the card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) throw new Error(error.message);

      // 3. Save the payment method ID on our server
      await saveLLCBillingCard({ payment_method_id: setupIntent?.payment_method }, (res: any, status: number) => {
        if (status === 200) {
          showAlert('success', `Card saved: ${res.card}`);
          onCardSaved();
        } else {
          throw new Error(res?.error || 'Failed to save card');
        }
      });
    } catch (err: any) {
      showAlert('error', err.message || 'Unexpected error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View style={styles.card}>
      <Text style={styles.sectionTitle}>💳 Add Billing Card</Text>
      <Text style={styles.hint}>
        This card will be charged by Bodhisys LLC for platform fees (Service Fee & BodhiProtect).
      </Text>
      <TextInput
        style={styles.input}
        placeholder="Business Name (optional)"
        value={tenantName}
        onChangeText={setTenantName}
        placeholderTextColor="#999"
      />
      <TextInput
        style={[styles.input, emailError ? { borderColor: '#DC3545', borderWidth: 1 } : {}]}
        placeholder="Billing Email (optional)"
        value={tenantEmail}
        onChangeText={(text) => { setTenantEmail(text); if (emailError) setEmailError(''); }}
        keyboardType="email-address"
        placeholderTextColor="#999"
      />
      {emailError ? <Text style={{ color: '#DC3545', fontSize: 11, marginTop: -4, marginBottom: 6 }}>{emailError}</Text> : null}
      {/* @stripe/react-stripe-js web CardElement */}
      <View style={styles.cardElementWrapper}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </View>
      <TouchableOpacity
        style={[styles.btn, styles.btnPrimary, isSaving && { opacity: 0.6 }]}
        onPress={handleSaveCard}
        disabled={isSaving}
      >
        {isSaving
          ? <ActivityIndicator color="#fff" />
          : <Text style={styles.btnText}>Save Card</Text>}
      </TouchableOpacity>
    </View>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

interface PlatformBillingScreenProps {
  navigation: any;
  openInventory?: (item: any) => void;
}

const PlatformBillingScreen = ({ navigation, openInventory }: PlatformBillingScreenProps) => {
  const { showAlert } = useAlertModal();

  // ── State ───────────────────────────────────────────────────────────────────
  const [llcPublicKey, setLLCPublicKey] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [billingStatus, setBillingStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showCardSetup, setShowCardSetup] = useState(false);

  // Admin controls
  const [billingCycle, setBillingCycle] = useState('weekly');
  const [graceDays, setGraceDays] = useState('3');
  const [cronHour, setCronHour] = useState('23');
  const [cronMinute, setCronMinute] = useState('0');
  const [cronInterval, setCronInterval] = useState('daily'); // 'seconds' | 'minutes' | 'daily'
  const [cronIntervalValue, setCronIntervalValue] = useState('30');
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // is_llc_master comes from the API (set via bodhisys_is_tenant = 'false' in settings_profile)
  const isLLCMaster = billingStatus?.is_llc_master === true;

  // LLC Master Admin
  const [overviewData, setOverviewData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'billing' | 'settings' | 'tenants'>('billing');
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const [serverOrders, setServerOrders] = useState<any[]>([]);
  const [serverOrderTotals, setServerOrderTotals] = useState<any | null>(null);
  const [loadingServerOrders, setLoadingServerOrders] = useState(false);
  const [isChargingManual, setIsChargingManual] = useState<string | null>(null);

  // Cron controls
  const [cronLogData, setCronLogData] = useState<any[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Brands pricing modal (master-controlled fees per brand)
  const [brandsModalServer, setBrandsModalServer] = useState<string | null>(null);
  const [brandsList, setBrandsList] = useState<any[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [savingBrandId, setSavingBrandId] = useState<number | null>(null);
  const [brandSearch, setBrandSearch] = useState('');
  const [brandSaveMsg, setBrandSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Cycle picker modal
  const [cycleModalServer, setCycleModalServer] = useState<string | null>(null);
  const [cycleModalCurrent, setCycleModalCurrent] = useState<string>('weekly');
  const [cycleModalIntervalValue, setCycleModalIntervalValue] = useState<string>('30');
  const [cycleModalHour, setCycleModalHour] = useState<string>('23');
  const [cycleModalMinute, setCycleModalMinute] = useState<string>('0');
  const [cycleModalSaving, setCycleModalSaving] = useState(false);

  // Unbilled orders breakdown (tenant view)
  const [unbilledOrders, setUnbilledOrders] = useState<any[]>([]);
  const [showUnbilledOrders, setShowUnbilledOrders] = useState(false);
  const [loadingUnbilled, setLoadingUnbilled] = useState(false);
  const [isChargingNow, setIsChargingNow] = useState(false);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null);

  // ── Data fetch ──────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    await getLLCPublicKey((res: any, status: number) => {
      if (status === 200 && res.public_key) {
        setLLCPublicKey(res.public_key);
        setStripePromise(loadStripe(res.public_key));
      }
    });
    let masterFlag = false;
    await getTenantBillingStatus((res: any, status: number) => {
      if (status === 200) {
        setBillingStatus(res);
        setBillingEnabled(res.billing_enabled);
        setBillingCycle(res.billing_cycle || 'weekly');
        setGraceDays(res.grace_days || '3');
        setCronHour(res.cron_hour || '23');
        setCronMinute(res.cron_minute || '0');
        setCronInterval(res.cron_interval || 'daily');
        setCronIntervalValue(res.cron_interval_value || '30');
        masterFlag = res.is_llc_master === true;
      }
    });
    // Fetch cross-tenant overview only if this is the LLC master instance
    if (masterFlag) {
      await getBillingOverview({}, (res: any, status: number) => {
        if (status === 200) setOverviewData(res.overview || []);
      });
      // Fetch cron logs
      await getCronLogsAPI((res: any, status: number) => {
        if (status === 200) setCronLogData(res.logs || []);
      });
    }
    // Switch to tenants tab for master AFTER all data is ready
    if (masterFlag) setActiveTab('tenants');
    setIsLoading(false);
    setInitialLoad(false);
  }, []);

  const fetchUnbilledOrders = useCallback(async (filter = '') => {
    setLoadingUnbilled(true);
    await getUnbilledOrders(filter ? { order_number: filter } : {}, (res: any, status: number) => {
      if (status === 200) setUnbilledOrders(res.orders || []);
    });
    setLoadingUnbilled(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh cron logs every 5 seconds for LLC Master on Settings tab
  useEffect(() => {
    if (!isLLCMaster || activeTab !== 'settings') return;
    
    const refreshLogs = async () => {
      await getCronLogsAPI((res: any, status: number) => {
        if (status === 200) setCronLogData(res.logs || []);
      });
    };

    const interval = setInterval(refreshLogs, 5000); // Every 5 seconds
    return () => clearInterval(interval);
  }, [isLLCMaster, activeTab]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleToggleBilling = async (val: boolean) => {
    setBillingEnabled(val);
    await toggleBillingEnabled({ enabled: val }, (res: any, status: number) => {
      if (status !== 200) showAlert('error', res?.error || 'Failed to update');
    });
  };

  const handleChargeNow = async () => {
    setIsChargingNow(true);
    await tenantTriggerCharge((res: any, status: number) => {
      if (res?.result?.success) {
        showAlert('success', `✅ Charged $${res.result.amount?.toFixed(2)}`);
        fetchAll();
      } else if (res?.result?.skipped) {
        // Show user-friendly message for skipped charges
        const message = res.result.reason === 'no_unbilled_reservations'
          ? 'No unbilled orders to charge at this time'
          : res.result.reason === 'billing_disabled'
          ? 'Auto-billing is currently disabled'
          : res.result.reason === 'no_card'
          ? 'Please add a payment card first'
          : res.result.reason;
        showAlert('info', message);
      } else {
        showAlert('error', res?.result?.reason || res?.error || 'Charge failed');
        fetchAll();
      }
    });
    setIsChargingNow(false);
  };
  const handleManualCharge = async (serverName: string) => {
    setIsChargingManual(serverName);
    await triggerManualCharge(serverName, (res: any, status: number) => {
      if (res?.success && res?.skipped) {
        // Nothing to charge - show info, not error
        showAlert('info', res.message || 'No unbilled orders at this time');
      } else if (res?.success) {
        // Successfully charged
        showAlert('success', res.message || `Charged $${res.amount?.toFixed(2)}`);
      } else {
        // Actual error
        showAlert('error', res?.message || res?.error || 'Charge failed');
      }
    });
    setIsChargingManual(null);
    fetchAll();
  };

  const handleViewOrders = async (serverName: string) => {
    setSelectedServer(serverName);
    setLoadingServerOrders(true);
    await getOrderDetail(serverName, {}, (res: any, status: number) => {
      if (status === 200) {
        setServerOrders(res.orders || []);
        setServerOrderTotals(res.totals || null);
      }
    });
    setLoadingServerOrders(false);
  };

  const handleOpenBrands = async (serverName: string) => {
    setBrandsModalServer(serverName);
    setBrandsList([]);
    setBrandSearch('');
    setLoadingBrands(true);
    await getTenantBrands(serverName, (res: any, status: number) => {
      if (status === 200) setBrandsList(res.brands || []);
      else showAlert('error', res?.error || 'Failed to load brands');
    });
    setLoadingBrands(false);
  };

  const handleBrandFieldChange = (brandId: number, field: string, value: any) => {
    setBrandsList((prev) =>
      prev.map((b) => (b.brand_id === brandId ? { ...b, [field]: value } : b))
    );
  };

  const handleSaveBrandFees = async (brand: any) => {
    if (!brandsModalServer) return;
    setSavingBrandId(brand.brand_id);
    setBrandSaveMsg(null);
    await updateTenantBrandFees(
      brandsModalServer,
      {
        brand_id: brand.brand_id,
        bodhi_protect_percentage: brand.bodhi_protect_percentage,
        service_fee_percentage: brand.service_fee_percentage,
        is_bodhi_protect_enabled: !!brand.is_bodhi_protect_enabled,
        is_service_fee_enabled: !!brand.is_service_fee_enabled,
        bodhi_protect_default: !!brand.bodhi_protect_default,
      },
      (res: any, status: number) => {
        setSavingBrandId(null); // Clear saving state after response
        if (status === 200) {
          const msg = `Pricing updated for ${displayName}`;
          setBrandSaveMsg({ type: 'success', text: msg });
        } else {
          const msg = res?.error || 'Failed to update pricing';
          setBrandSaveMsg({ type: 'error', text: msg });
        }
        setTimeout(() => setBrandSaveMsg(null), 4000);
      }
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading && initialLoad) {
    return (
      <BasicLayout navigation={navigation} screenName="Platform Billing">
        <ActivityIndicator size="large" color="#0063ff" style={{ marginTop: 40 }} />
      </BasicLayout>
    );
  }

  return (
    <BasicLayout navigation={navigation} screenName="Platform Billing">
      <ScrollView contentContainerStyle={styles.container}>

        {/* ── Tab Bar ─────────────────────────────────── */}
        <View style={styles.tabBar}>
          {isLLCMaster && (
            <TouchableOpacity
              style={[styles.tab, activeTab === 'tenants' && styles.tabActive]}
              onPress={() => setActiveTab('tenants')}
            >
              <Text style={[styles.tabText, activeTab === 'tenants' && styles.tabTextActive]}>
                🏢 All Tenants ({overviewData.length})
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.tab, activeTab === 'billing' && styles.tabActive]}
            onPress={() => setActiveTab('billing')}
          >
            <Text style={[styles.tabText, activeTab === 'billing' && styles.tabTextActive]}>My Billing</Text>
          </TouchableOpacity>
          {isLLCMaster && (
            <TouchableOpacity
              style={[styles.tab, activeTab === 'settings' && styles.tabActive]}
              onPress={() => setActiveTab('settings')}
            >
              <Text style={[styles.tabText, activeTab === 'settings' && styles.tabTextActive]}>📋 Cron Logs</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* ══════════════════════════════════════════════════════════════════
            LLC MASTER - CRON LOGS TAB
        ══════════════════════════════════════════════════════════════════ */}
        {isLLCMaster && activeTab === 'settings' && (
          <>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 8 }}>
              <TouchableOpacity onPress={() => fetchAll()} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#f0f4ff', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 }}>
                <FontAwesome5 name="sync-alt" size={12} color="#0063ff" />
                <Text style={{ fontSize: 12, color: '#0063ff', fontWeight: '600' }}>Refresh</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.card}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={styles.sectionTitle}>📋 Cron Execution Logs</Text>
                <View style={{ backgroundColor: '#0f0', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 }}>
                  <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#000' }}>● LIVE</Text>
                </View>
              </View>
              
              <ScrollView 
                style={{ backgroundColor: '#1a1a2e', borderRadius: 8, padding: 12, marginBottom: 12, maxHeight: 400 }}
                nestedScrollEnabled={true}
              >
                {cronLogData.length === 0 ? (
                  <Text style={{ fontFamily: 'monospace', fontSize: 11, color: '#888' }}>
                    No cron logs yet. Logs will appear here when the billing cron runs.
                  </Text>
                ) : (
                  cronLogData.map((log: any, i: number) => {
                    const color = log.level === 'success' ? '#0f0' : log.level === 'error' ? '#f55' : log.level === 'warn' ? '#ff0' : '#fff';
                    const ts = new Date(log.timestamp).toLocaleString();
                    return (
                      <Text key={i} style={{ fontFamily: 'monospace', fontSize: 11, color, marginBottom: 3 }}>
                        [{ts}] {log.message}
                      </Text>
                    );
                  })
                )}
              </ScrollView>
              
              <TouchableOpacity
                style={[styles.btn, styles.btnSecondary, isLoadingLogs && { opacity: 0.6 }]}
                onPress={async () => {
                  setIsLoadingLogs(true);
                  await getCronLogsAPI((res: any, status: number) => {
                    if (status === 200) setCronLogData(res.logs || []);
                  });
                  setIsLoadingLogs(false);
                }}
                disabled={isLoadingLogs}
              >
                {isLoadingLogs ? (
                  <ActivityIndicator color="#0063ff" size="small" />
                ) : (
                  <>
                    <FontAwesome5 name="sync" size={14} color="#0063ff" />
                    <Text style={[styles.btnText, { color: '#0063ff', marginLeft: 8 }]}>Refresh Logs</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            LLC MASTER - ALL TENANTS TAB
        ══════════════════════════════════════════════════════════════════ */}
        {isLLCMaster && activeTab === 'tenants' && (
          <>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 8 }}>
              <TouchableOpacity onPress={() => fetchAll()} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#f0f4ff', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 }}>
                <FontAwesome5 name="sync-alt" size={12} color="#0063ff" />
                <Text style={{ fontSize: 12, color: '#0063ff', fontWeight: '600' }}>Refresh</Text>
              </TouchableOpacity>
            </View>
            {selectedServer ? (
              <View>
                <TouchableOpacity style={styles.backBtn} onPress={() => { setSelectedServer(null); setServerOrders([]); setServerOrderTotals(null); }}>
                  <FontAwesome5 name="arrow-left" size={14} color="#0063ff" />
                  <Text style={{ color: '#0063ff', marginLeft: 6 }}>Back to Overview</Text>
                </TouchableOpacity>
                <Text style={styles.serverTitle}>{selectedServer} — Orders ({serverOrders.length})</Text>
                {loadingServerOrders ? (
                  <ActivityIndicator size="large" color="#0063ff" />
                ) : (
                  <View style={{ backgroundColor: '#fff', borderRadius: 8, padding: 12, marginTop: 10 }}>
                    {/* Table Header */}
                    <View style={{ flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#e0e0e0', paddingBottom: 8, marginBottom: 8 }}>
                      <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 12 }}>Order #</Text>
                      <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 12, textAlign: 'right' }}>BodhiProtect</Text>
                      <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 12, textAlign: 'right' }}>Service</Text>
                      <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 12, textAlign: 'right' }}>Total</Text>
                      <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 12, textAlign: 'right' }}>Remaining</Text>
                      <Text style={{ flex: 1.2, fontWeight: 'bold', fontSize: 12, textAlign: 'center' }}>Status</Text>
                    </View>
                    {/* Table Rows */}
                    {serverOrders.map((order: any) => (
                      <View key={order.id} style={{ flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' }}>
                        <Text style={{ flex: 1.5, fontSize: 13 }}>#{order.order_number}</Text>
                        <Text style={{ flex: 1, fontSize: 13, textAlign: 'right' }}>${parseFloat(order.bodhi_protect_price || 0).toFixed(2)}</Text>
                        <Text style={{ flex: 1, fontSize: 13, textAlign: 'right' }}>${parseFloat(order.service_fee || 0).toFixed(2)}</Text>
                        <Text style={{ flex: 1, fontSize: 13, textAlign: 'right', fontWeight: 'bold' }}>${parseFloat(order.llc_fee_total || 0).toFixed(2)}</Text>
                        <Text style={{ flex: 1, fontSize: 13, textAlign: 'right', fontWeight: 'bold', color: parseFloat(order.llc_remaining || 0) > 0 ? '#DC3545' : '#bbb' }}>
                          ${parseFloat(order.llc_remaining || 0).toFixed(2)}
                        </Text>
                        <Text style={{ flex: 1.2, fontSize: 11, textAlign: 'center', fontWeight: 'bold', color: parseFloat(order.llc_remaining || 0) > 0 ? '#DC3545' : (STATUS_COLORS[order.llc_remittance_status] || '#000') }}>
                          {parseFloat(order.llc_remaining || 0) > 0 ? 'DUE' : order.llc_remittance_status}
                        </Text>
                      </View>
                    ))}
                    {/* Summary Row */}
                    <View style={{ flexDirection: 'row', paddingTop: 12, marginTop: 8, borderTopWidth: 2, borderTopColor: '#0063ff' }}>
                      <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 14 }}>TOTAL</Text>
                      <Text style={{ flex: 1, fontSize: 14, textAlign: 'right', fontWeight: 'bold' }}>
                        ${(serverOrderTotals ? serverOrderTotals.total_bodhi_protect : serverOrders.reduce((sum: number, o: any) => sum + parseFloat(o.bodhi_protect_price || 0), 0)).toFixed(2)}
                      </Text>
                      <Text style={{ flex: 1, fontSize: 14, textAlign: 'right', fontWeight: 'bold' }}>
                        ${(serverOrderTotals ? serverOrderTotals.total_service_fee : serverOrders.reduce((sum: number, o: any) => sum + parseFloat(o.service_fee || 0), 0)).toFixed(2)}
                      </Text>
                      <Text style={{ flex: 1, fontSize: 14, textAlign: 'right', fontWeight: 'bold', color: '#0063ff' }}>
                        ${(serverOrderTotals ? serverOrderTotals.total_fee : serverOrders.reduce((sum: number, o: any) => sum + parseFloat(o.llc_fee_total || 0), 0)).toFixed(2)}
                      </Text>
                      <Text style={{ flex: 1, fontSize: 14, textAlign: 'right', fontWeight: 'bold', color: '#DC3545' }}>
                        ${(serverOrderTotals ? serverOrderTotals.total_remaining : serverOrders.reduce((sum: number, o: any) => sum + parseFloat(o.llc_remaining || 0), 0)).toFixed(2)}
                      </Text>
                      <Text style={{ flex: 1.2 }}></Text>
                    </View>
                  </View>
                )}
              </View>
            ) : (
              <View style={{ backgroundColor: '#fff', borderRadius: 8, padding: 12, marginTop: 10, overflow: 'visible', zIndex: 100 }}>
                {/* Table Header */}
                <View style={{ flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#0063ff', paddingBottom: 10, marginBottom: 10 }}>
                  <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 13 }}>Tenant</Text>
                  <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 13 }}>Card</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 13, textAlign: 'right' }}>Outstanding</Text>
                  <Text style={{ flex: 0.8, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Orders</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Cycle</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Auto-Bill</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Billing Since</Text>
                  <Text style={{ flex: 0.8, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Failures</Text>
                  <Text style={{ flex: 0.8, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Threshold</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Locked</Text>
                  <Text style={{ flex: 2.2, fontWeight: 'bold', fontSize: 13, textAlign: 'center' }}>Actions</Text>
                </View>
                
                {/* Table Rows */}
                {overviewData.map((item: any, idx: number) => (
                  <View key={item.server} style={{ flexDirection: 'row', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0', alignItems: 'center', overflow: 'visible', zIndex: 100 - idx }}>
                    {/* Tenant Name */}
                    <View style={{ flex: 1.5 }}>
                      <Text style={{ fontSize: 14, fontWeight: 'bold', color: item.platform_locked ? '#DC3545' : '#222' }}>
                        {item.server}
                      </Text>
                      {item.platform_locked && <Text style={{ fontSize: 10, color: '#DC3545' }}>🔒 LOCKED</Text>}
                    </View>
                    
                    {/* Card */}
                    <Text style={{ flex: 1.5, fontSize: 12, color: item.card_on_file ? '#28A745' : '#DC3545' }}>
                      {item.card_on_file || '❌ None'}
                    </Text>
                    
                    {/* Outstanding */}
                    <Text style={{ flex: 1, fontSize: 13, textAlign: 'right', fontWeight: 'bold', color: item.outstanding_amount > 0 ? '#DC3545' : '#666' }}>
                      ${item.outstanding_amount?.toFixed(2)}
                    </Text>
                    
                    {/* Orders */}
                    <Text style={{ flex: 0.8, fontSize: 13, textAlign: 'center' }}>
                      {item.unbilled_order_count}
                    </Text>
                    
                    {/* Cycle (tap to open picker) */}
                    <TouchableOpacity
                      style={{ flex: 1, alignItems: 'center' }}
                      onPress={() => {
                        setCycleModalServer(item.server);
                        setCycleModalCurrent(item.billing_cycle || 'daily');
                        setCycleModalIntervalValue(item.cron_interval_value || '30');
                        setCycleModalHour(item.cron_hour || '23');
                        setCycleModalMinute(item.cron_minute || '0');
                      }}
                    >
                      <Text style={{ fontSize: 11, textAlign: 'center', color: '#0063ff', textDecorationLine: 'underline', fontWeight: '600' }}>
                        {item.billing_cycle}
                      </Text>
                      <Text style={{ fontSize: 9, textAlign: 'center', color: '#888', marginTop: 2 }}>
                        {item.billing_cycle === 'daily'
                          ? `${(item.cron_hour || '23').padStart(2, '0')}:${(item.cron_minute || '0').padStart(2, '0')} EST`
                          : item.billing_cycle === 'minutes'
                          ? `every ${item.cron_interval_value || '30'} min`
                          : item.billing_cycle === 'per_order'
                          ? 'immediate'
                          : ''}
                      </Text>
                    </TouchableOpacity>
                    
                    {/* Auto-Billing Toggle */}
                    <View style={{ flex: 1, alignItems: 'center' }}>
                      <Switch
                        value={item.billing_enabled}
                        onValueChange={(val) => {
                          updateBillingSettings(item.server, { llc_billing_enabled: val ? 'true' : 'false' }, (res: any, status: number) => {
                            if (status === 200) {
                              showAlert('success', `Auto-billing ${val ? 'enabled' : 'disabled'} for ${item.server}`);
                              fetchAll();
                            } else {
                              showAlert('error', 'Failed to update');
                            }
                          });
                        }}
                        trackColor={{ true: '#28A745', false: '#ccc' }}
                      />
                    </View>

                    {/* Billing Start Date */}
                    <View style={{ flex: 1, alignItems: 'center', zIndex: 999, overflow: 'visible', position: 'relative' }}>
                      {renderBOHTlbDatePicker(
                        item.billing_start_date || '',
                        (date: Date) => {
                          const yyyy = date.getFullYear();
                          const mm = String(date.getMonth() + 1).padStart(2, '0');
                          const dd = String(date.getDate()).padStart(2, '0');
                          const val = `${yyyy}-${mm}-${dd}`;
                          updateBillingSettings(item.server, { llc_billing_start_date: val }, (res: any, status: number) => {
                            if (status === 200) {
                              showAlert('success', `Billing start date set to ${val}`);
                              fetchAll();
                            } else {
                              showAlert('error', 'Failed to update');
                            }
                          });
                        },
                        { fontSize: 10, width: 80, textAlign: 'center' }
                      )}
                    </View>
                    
                    {/* Failures Count */}
                    <View style={{ flex: 0.8, alignItems: 'center' }}>
                      <Text style={{
                        fontSize: 13,
                        fontWeight: 'bold',
                        color: item.consecutive_failures > 0 ? '#DC3545' : '#28A745',
                      }}>
                        {item.consecutive_failures || 0}
                      </Text>
                    </View>

                    {/* Failure Threshold (editable) */}
                    <View style={{ flex: 0.8, alignItems: 'center' }}>
                      <TextInput
                        style={{
                          borderWidth: 1,
                          borderColor: '#ddd',
                          borderRadius: 4,
                          paddingHorizontal: 6,
                          paddingVertical: 3,
                          fontSize: 12,
                          textAlign: 'center',
                          width: 45,
                          backgroundColor: '#f9f9f9',
                        }}
                        keyboardType="numeric"
                        defaultValue={String(item.failure_threshold || 3)}
                        onBlur={(e) => {
                          const val = e.nativeEvent.text;
                          if (val && parseInt(val) !== item.failure_threshold) {
                            updateBillingSettings(item.server, { llc_failure_threshold: val }, (res: any, status: number) => {
                              if (status === 200) {
                                showAlert('success', `Failure threshold set to ${val} for ${item.server}`);
                                fetchAll();
                              } else {
                                showAlert('error', 'Failed to update threshold');
                              }
                            });
                          }
                        }}
                      />
                    </View>

                    {/* Platform Lockout Toggle */}
                    <View style={{ flex: 1, alignItems: 'center' }}>
                      <Switch
                        value={item.platform_locked}
                        onValueChange={(val) => {
                          updateBillingSettings(item.server, { platform_lockout: val ? 'true' : 'false' }, (res: any, status: number) => {
                            if (status === 200) {
                              showAlert('success', `Platform ${val ? 'locked' : 'unlocked'} for ${item.server}`);
                              fetchAll();
                            } else {
                              showAlert('error', 'Failed to update');
                            }
                          });
                        }}
                        trackColor={{ true: '#DC3545', false: '#ccc' }}
                      />
                    </View>
                    
                    {/* Actions */}
                    <View style={{ flex: 2.2, flexDirection: 'row', gap: 4 }}>
                      <TouchableOpacity
                        style={{ backgroundColor: '#0063ff', paddingVertical: 6, paddingHorizontal: 8, borderRadius: 4, flex: 1 }}
                        onPress={() => handleViewOrders(item.server)}
                      >
                        <Text style={{ color: '#fff', fontSize: 10, textAlign: 'center' }}>View</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={{ backgroundColor: '#6f42c1', paddingVertical: 6, paddingHorizontal: 8, borderRadius: 4, flex: 1 }}
                        onPress={() => handleOpenBrands(item.server)}
                      >
                        <Text style={{ color: '#fff', fontSize: 10, textAlign: 'center' }}>Brands</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={{ backgroundColor: '#28A745', paddingVertical: 6, paddingHorizontal: 8, borderRadius: 4, flex: 1 }}
                        onPress={() => handleManualCharge(item.server)}
                        disabled={isChargingManual === item.server}
                      >
                        {isChargingManual === item.server ? (
                          <ActivityIndicator color="#fff" size="small" />
                        ) : (
                          <Text style={{ color: '#fff', fontSize: 10, textAlign: 'center' }}>Charge</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            MY BILLING TAB (shown for both master and tenants)
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'billing' && (
          <>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 8 }}>
              <TouchableOpacity onPress={() => fetchAll()} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#f0f4ff', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 }}>
                <FontAwesome5 name="sync-alt" size={12} color="#0063ff" />
                <Text style={{ fontSize: 12, color: '#0063ff', fontWeight: '600' }}>Refresh</Text>
              </TouchableOpacity>
            </View>
            {/* ── Two-column: Billing Status (left) + Card (right) ─────── */}
            <View style={{ flexDirection: 'row', gap: 16, marginBottom: 16 }}>
              {/* LEFT — Billing Status */}
              <View style={[styles.card, { flex: 1, marginBottom: 0 }]}>
                <Text style={styles.sectionTitle}>📋 Billing Status</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>Card on File</Text>
                  <Text style={[styles.value, !billingStatus?.card_on_file && { color: '#DC3545' }]}>
                    {billingStatus?.card_on_file || 'No card saved'}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Outstanding</Text>
                  <Text style={styles.valueHighlight}>${billingStatus?.outstanding_amount?.toFixed(2) || '0.00'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Next Payout</Text>
                  <Text style={styles.value}>{billingStatus?.next_payout === 'Not scheduled' ? 'Not scheduled' : billingStatus?.next_payout ? new Date(billingStatus.next_payout).toLocaleDateString() : '—'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Billing Cycle</Text>
                  <View style={{ backgroundColor: '#f0f0f0', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 }}>
                    <Text style={{ fontSize: 13, fontWeight: 'bold', color: '#333', textTransform: 'capitalize' }}>
                      {billingStatus?.billing_cycle || 'weekly'}
                    </Text>
                  </View>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Auto-Billing</Text>
                  <View style={{ backgroundColor: billingStatus?.billing_enabled ? '#e8f5e9' : '#fce4ec', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 }}>
                    <Text style={{ fontSize: 13, fontWeight: 'bold', color: billingStatus?.billing_enabled ? '#2e7d32' : '#c62828' }}>
                      {billingStatus?.billing_enabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  </View>
                </View>
                <Text style={{ fontSize: 11, color: '#888', marginTop: 4, fontStyle: 'italic' }}>
                  * Billing settings are managed by the platform administrator
                </Text>
                {billingStatus?.platform_locked && (
                  <View style={styles.warningBanner}>
                    <FontAwesome5 name="lock" size={14} color="#fff" />
                    <Text style={styles.warningText}>  Account suspended — update your card to restore service.</Text>
                  </View>
                )}
                <Text style={styles.disclaimer}>{billingStatus?.disclaimer}</Text>
              </View>

              {/* RIGHT — Card & Setup */}
              <View style={{ flex: 1 }}>
                {billingStatus?.card_on_file && (
                  <LinearGradient
                    colors={['#667eea', '#764ba2']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.creditCard, { marginBottom: 12 }]}
                  >
                    <View style={{ alignItems: 'flex-end', marginBottom: 20 }}>
                      <View style={styles.cardBrand}>
                        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff' }}>
                          {billingStatus.card_on_file.includes('visa') ? 'VISA' : 
                           billingStatus.card_on_file.includes('mastercard') ? 'Mastercard' :
                           billingStatus.card_on_file.includes('amex') ? 'AMEX' : 'CARD'}
                        </Text>
                      </View>
                    </View>
                    <View style={{ marginBottom: 20 }}>
                      <Text style={styles.cardNumber}>
                        •••• •••• •••• {billingStatus.card_on_file.match(/\d+/)?.[0] || '****'}
                      </Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <View>
                        <Text style={styles.cardLabel}>CARDHOLDER</Text>
                        <Text style={styles.cardValue}>Platform Billing</Text>
                      </View>
                      <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.cardLabel}>VALID THRU</Text>
                        <Text style={styles.cardValue}>••/••</Text>
                      </View>
                    </View>
                    <View style={styles.cardChip}>
                      <FontAwesome5 name="microchip" size={24} color="#FFD700" />
                    </View>
                  </LinearGradient>
                )}

                {stripePromise && (
                  !showCardSetup ? (
                    <TouchableOpacity style={[styles.btn, styles.btnSecondary]} onPress={() => setShowCardSetup(true)}>
                      <FontAwesome5 name="credit-card" size={14} color="#0063ff" />
                      <Text style={[styles.btnText, { color: '#0063ff', marginLeft: 8 }]}>
                        {billingStatus?.card_on_file ? 'Update Billing Card' : 'Add Billing Card'}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <Elements stripe={stripePromise}>
                      <CardSetupForm onCardSaved={() => { setShowCardSetup(false); fetchAll(); }} />
                    </Elements>
                  )
                )}
              </View>
            </View>

            {/* ── Invoice History Data Table ───────────────────────────── */}
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>🧾 Invoice History</Text>
              {(!billingStatus?.invoices || billingStatus.invoices.length === 0) ? (
                <Text style={styles.emptyText}>No invoices yet.</Text>
              ) : (
                <View>
                  {/* Table Header */}
                  <View style={{ flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#e0e0e0', paddingBottom: 10, marginBottom: 4 }}>
                    <Text style={{ flex: 1.2, fontWeight: 'bold', fontSize: 12, color: '#555' }}>Date</Text>
                    <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 12, color: '#555' }}>Billing Period</Text>
                    <Text style={{ flex: 0.8, fontWeight: 'bold', fontSize: 12, color: '#555', textAlign: 'center' }}>Orders</Text>
                    <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 12, color: '#555', textAlign: 'right' }}>Amount</Text>
                    <Text style={{ flex: 0.8, fontWeight: 'bold', fontSize: 12, color: '#555', textAlign: 'center' }}>Status</Text>
                    <Text style={{ flex: 0.5, fontWeight: 'bold', fontSize: 12, color: '#555', textAlign: 'center' }}></Text>
                  </View>

                  {/* Table Rows */}
                  {billingStatus.invoices.map((inv: any) => {
                    const isExpanded = expandedInvoiceId === inv.id;
                    const orderCount = inv.metadata?.order_ids?.length || inv.metadata?.order_numbers?.length || 0;
                    const breakdown = inv.metadata?.breakdown || [];
                    return (
                      <View key={inv.id}>
                        <TouchableOpacity
                          style={{ flexDirection: 'row', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0', alignItems: 'center' }}
                          onPress={() => setExpandedInvoiceId(isExpanded ? null : inv.id)}
                          activeOpacity={0.7}
                        >
                          <Text style={{ flex: 1.2, fontSize: 13, color: '#333' }}>
                            {inv.date ? new Date(inv.date).toLocaleDateString() : '—'}
                          </Text>
                          <Text style={{ flex: 1.5, fontSize: 11, color: '#888' }}>
                            {inv.period_start && inv.period_end
                              ? `${new Date(inv.period_start).toLocaleDateString()} → ${new Date(inv.period_end).toLocaleDateString()}`
                              : '—'}
                          </Text>
                          <Text style={{ flex: 0.8, fontSize: 13, textAlign: 'center', color: '#333' }}>
                            {orderCount}
                          </Text>
                          <Text style={{ flex: 1, fontSize: 14, textAlign: 'right', fontWeight: 'bold', color: '#222' }}>
                            ${parseFloat(inv.amount).toFixed(2)}
                          </Text>
                          <View style={{ flex: 0.8, alignItems: 'center' }}>
                            <Text style={{ fontSize: 11, fontWeight: 'bold', color: STATUS_COLORS[inv.status] || '#000' }}>
                              {inv.status}
                            </Text>
                          </View>
                          <View style={{ flex: 0.5, alignItems: 'center' }}>
                            <FontAwesome5
                              name={isExpanded ? 'chevron-up' : 'chevron-down'}
                              size={12}
                              color="#0063ff"
                            />
                          </View>
                        </TouchableOpacity>

                        {/* Expanded: Order Breakdown + Payment Info */}
                        {isExpanded && breakdown.length > 0 && (
                          <View style={{ backgroundColor: '#f8f9fc', borderRadius: 8, padding: 12, marginVertical: 8, borderWidth: 1, borderColor: '#e8ecf4' }}>
                            {/* Breakdown Header */}
                            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#dde2ed', paddingBottom: 8, marginBottom: 6 }}>
                              <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 11, color: '#666' }}>Order #</Text>
                              <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 11, color: '#666', textAlign: 'right' }}>BodhiProtect</Text>
                              <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 11, color: '#666', textAlign: 'right' }}>Service Fee</Text>
                              <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 11, color: '#666', textAlign: 'right' }}>Total</Text>
                            </View>
                            {/* Breakdown Rows */}
                            <ScrollView style={{ maxHeight: 400 }} nestedScrollEnabled>
                              {breakdown.map((row: any) => {
                                const bp = parseFloat(row.bodhi_protect || 0);
                                const sf = parseFloat(row.service_fee || 0);
                                return (
                                  <View key={row.id} style={{ flexDirection: 'row', paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: '#eef1f6', alignItems: 'center' }}>
                                    <Text style={{ flex: 1.5, fontSize: 12, color: '#333', fontWeight: '600' }}>#{row.order_number}</Text>
                                    <Text style={{ flex: 1, fontSize: 12, color: '#555', textAlign: 'right' }}>${bp.toFixed(2)}</Text>
                                    <Text style={{ flex: 1, fontSize: 12, color: '#555', textAlign: 'right' }}>${sf.toFixed(2)}</Text>
                                    <Text style={{ flex: 1, fontSize: 12, color: '#222', textAlign: 'right', fontWeight: '600' }}>${(bp + sf).toFixed(2)}</Text>
                                  </View>
                                );
                              })}
                            </ScrollView>
                            {/* Breakdown Totals */}
                            <View style={{ flexDirection: 'row', paddingTop: 10, marginTop: 6, borderTopWidth: 2, borderTopColor: '#0063ff' }}>
                              <Text style={{ flex: 1.5, fontWeight: 'bold', fontSize: 12, color: '#222' }}>TOTAL ({breakdown.length} orders)</Text>
                              <Text style={{ flex: 1, fontSize: 12, fontWeight: 'bold', textAlign: 'right', color: '#222' }}>
                                ${breakdown.reduce((s: number, r: any) => s + parseFloat(r.bodhi_protect || 0), 0).toFixed(2)}
                              </Text>
                              <Text style={{ flex: 1, fontSize: 12, fontWeight: 'bold', textAlign: 'right', color: '#222' }}>
                                ${breakdown.reduce((s: number, r: any) => s + parseFloat(r.service_fee || 0), 0).toFixed(2)}
                              </Text>
                              <Text style={{ flex: 1, fontSize: 12, fontWeight: 'bold', textAlign: 'right', color: '#0063ff' }}>
                                ${breakdown.reduce((s: number, r: any) => s + parseFloat(r.bodhi_protect || 0) + parseFloat(r.service_fee || 0), 0).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                        )}
                        {isExpanded && breakdown.length === 0 && (
                          <View style={{ backgroundColor: '#f8f9fc', borderRadius: 8, padding: 16, marginVertical: 8 }}>
                            <Text style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>No order breakdown available for this invoice.</Text>
                          </View>
                        )}
                        {inv.failure_reason && isExpanded && (
                          <Text style={{ fontSize: 11, color: '#DC3545', marginBottom: 8, paddingHorizontal: 4 }}>
                            Failure: {inv.failure_reason}
                          </Text>
                        )}
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          </>
        )}
      </ScrollView>

      {/* ── Brand Pricing Modal (master-controlled fees) ───────────────── */}
      <Modal
        visible={!!brandsModalServer}
        transparent
        animationType="fade"
        onRequestClose={() => setBrandsModalServer(null)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '95%', maxWidth: 900, maxHeight: '92%' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ fontSize: 20, fontWeight: '700', color: '#1a1a2e' }}>Brand Pricing</Text>
              <TouchableOpacity onPress={() => setBrandsModalServer(null)}>
                <FontAwesome5 name="times" size={20} color="#888" />
              </TouchableOpacity>
            </View>
            <Text style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
              Control BodhiProtect &amp; Service Fee rates and enable/disable per brand on{' '}
              <Text style={{ fontWeight: '700', color: '#6f42c1' }}>{brandsModalServer}</Text>. These are
              locked on the tenant's side.
            </Text>

            {/* Search */}
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f4f5f7', borderRadius: 10, paddingHorizontal: 12, marginBottom: 14 }}>
              <FontAwesome5 name="search" size={13} color="#999" />
              <TextInput
                style={{ flex: 1, paddingVertical: 10, paddingHorizontal: 10, fontSize: 14, color: '#222' }}
                placeholder="Search brands..."
                placeholderTextColor="#999"
                value={brandSearch}
                onChangeText={setBrandSearch}
              />
              {brandSearch ? (
                <TouchableOpacity onPress={() => setBrandSearch('')}>
                  <FontAwesome5 name="times-circle" size={14} color="#bbb" />
                </TouchableOpacity>
              ) : null}
            </View>

            {brandSaveMsg ? (
              <View style={{
                flexDirection: 'row', alignItems: 'center', gap: 8,
                backgroundColor: brandSaveMsg.type === 'success' ? '#E6F7EC' : '#FDECEC',
                borderWidth: 1,
                borderColor: brandSaveMsg.type === 'success' ? '#28A745' : '#DC3545',
                borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, marginBottom: 12,
              }}>
                <FontAwesome5
                  name={brandSaveMsg.type === 'success' ? 'check-circle' : 'exclamation-circle'}
                  size={14}
                  color={brandSaveMsg.type === 'success' ? '#28A745' : '#DC3545'}
                  solid
                />
                <Text style={{ flex: 1, fontSize: 13, fontWeight: '600', color: brandSaveMsg.type === 'success' ? '#1e7e34' : '#b02a37' }}>
                  {brandSaveMsg.text}
                </Text>
                <TouchableOpacity onPress={() => setBrandSaveMsg(null)}>
                  <FontAwesome5 name="times" size={13} color="#999" />
                </TouchableOpacity>
              </View>
            ) : null}

            {loadingBrands ? (
              <ActivityIndicator size="large" color="#6f42c1" style={{ marginVertical: 40 }} />
            ) : brandsList.length === 0 ? (
              <Text style={{ color: '#aaa', textAlign: 'center', paddingVertical: 30 }}>No brands found.</Text>
            ) : (
              <ScrollView style={{ maxHeight: 540 }} nestedScrollEnabled>
                {/* Header */}
                <View style={{ flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#0063ff', paddingBottom: 10, marginBottom: 4 }}>
                  <Text style={{ flex: 2.6, fontWeight: 'bold', fontSize: 13, color: '#222' }}>Brand</Text>
                  <Text style={{ flex: 2, fontWeight: 'bold', fontSize: 13, color: '#222', textAlign: 'center' }}>BodhiProtect % Charge</Text>
                  <Text style={{ flex: 1.3, fontWeight: 'bold', fontSize: 13, color: '#222', textAlign: 'center' }}>Service Fee % Charge</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 13, color: '#222', textAlign: 'center' }}></Text>
                </View>
                {brandsList
                  .filter((b: any) => {
                    const q = brandSearch.trim().toLowerCase();
                    if (!q) return true;
                    const name = (b.brand_name || b.store_name || `brand #${b.brand_id}`).toLowerCase();
                    return name.includes(q);
                  })
                  .map((brand: any) => {
                    const hasStore = brand.store_name !== null && brand.store_name !== undefined;
                    const bpOn = !!brand.is_bodhi_protect_enabled;
                    const sfOn = !!brand.is_service_fee_enabled;
                    const bpDefault = brand.bodhi_protect_default ? 1 : 0;
                    const displayName = brand.brand_name || brand.store_name || `Brand #${brand.brand_id}`;
                    return (
                      <View key={brand.brand_id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' }}>
                        {/* Brand name + store url */}
                        <View style={{ flex: 2.6, paddingRight: 8 }}>
                          <Text style={{ fontSize: 14, fontWeight: '600', color: '#222' }}>
                            {displayName}
                          </Text>
                          {brand.store_url ? (
                            <TouchableOpacity
                              onPress={() => {
                                const raw = String(brand.store_url).trim();
                                const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                                Linking.openURL(url).catch(() => showAlert('error', 'Unable to open link'));
                              }}
                            >
                              <Text style={{ fontSize: 11, color: '#0063ff', textDecorationLine: 'underline' }} numberOfLines={1}>
                                {brand.store_url}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                          {!hasStore && <Text style={{ fontSize: 10, color: '#DC3545' }}>No store details</Text>}
                        </View>

                        {/* BodhiProtect: toggle + % + opt-in/out default */}
                        <View style={{ flex: 2, alignItems: 'center', gap: 6 }}>
                          <Switch
                            value={bpOn}
                            disabled={!hasStore}
                            onValueChange={(val) => handleBrandFieldChange(brand.brand_id, 'is_bodhi_protect_enabled', val ? 1 : 0)}
                            trackColor={{ true: '#28A745', false: '#ccc' }}
                          />
                          <TextInput
                            style={{ borderWidth: 1, borderColor: '#ddd', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 6, fontSize: 13, textAlign: 'center', width: 72, backgroundColor: hasStore && bpOn ? '#fff' : '#f0f0f0', color: bpOn ? '#222' : '#999' }}
                            keyboardType="numeric"
                            editable={hasStore && bpOn}
                            value={bpOn ? String(brand.bodhi_protect_percentage ?? '') : '0.00'}
                            onChangeText={(t) => handleBrandFieldChange(brand.brand_id, 'bodhi_protect_percentage', t)}
                          />
                          {hasStore && bpOn && (
                            <View style={{ width: 200 }}>
                              <Select
                                label=""
                                options={[
                                  { label: 'OPT-OUT (Default ON)', value: 1 },
                                  { label: 'OPT-IN (Default OFF)', value: 0 },
                                ]}
                                value={bpDefault}
                                onChange={(newValue) => handleBrandFieldChange(brand.brand_id, 'bodhi_protect_default', newValue)}
                                placeholder="Default"
                                selectedTextProps={{ numberOfLines: 1 }}
                                customStyle={{
                                  input: { minHeight: 34, marginTop: 0, marginBottom: 0, paddingVertical: 4, paddingHorizontal: 10, borderRadius: 6, borderColor: '#ddd' },
                                  selectedTextStyle: { fontSize: 12 },
                                  renderItemText: { fontSize: 12 },
                                  renderItemContainer: { paddingVertical: 10, paddingHorizontal: 12 },
                                }}
                              />
                            </View>
                          )}
                        </View>

                        {/* Service Fee: toggle + % */}
                        <View style={{ flex: 1.3, alignItems: 'center', gap: 6 }}>
                          <Switch
                            value={sfOn}
                            disabled={!hasStore}
                            onValueChange={(val) => handleBrandFieldChange(brand.brand_id, 'is_service_fee_enabled', val ? 1 : 0)}
                            trackColor={{ true: '#28A745', false: '#ccc' }}
                          />
                          <TextInput
                            style={{ borderWidth: 1, borderColor: '#ddd', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 6, fontSize: 13, textAlign: 'center', width: 72, backgroundColor: hasStore && sfOn ? '#fff' : '#f0f0f0', color: sfOn ? '#222' : '#999' }}
                            keyboardType="numeric"
                            editable={hasStore && sfOn}
                            value={sfOn ? String(brand.service_fee_percentage ?? '') : '0.00'}
                            onChangeText={(t) => handleBrandFieldChange(brand.brand_id, 'service_fee_percentage', t)}
                          />
                        </View>

                        {/* Save */}
                        <View style={{ flex: 1, alignItems: 'center' }}>
                          <TouchableOpacity
                            style={{ backgroundColor: hasStore ? '#28A745' : '#ccc', paddingVertical: 8, paddingHorizontal: 14, borderRadius: 6 }}
                            disabled={!hasStore || savingBrandId === brand.brand_id}
                            onPress={() => handleSaveBrandFees(brand)}
                          >
                            {savingBrandId === brand.brand_id ? (
                              <ActivityIndicator color="#fff" size="small" />
                            ) : (
                              <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Save</Text>
                            )}
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })}
              </ScrollView>
            )}

            <TouchableOpacity
              style={{ marginTop: 18, paddingVertical: 12, borderRadius: 10, backgroundColor: '#f0f0f0', alignItems: 'center' }}
              onPress={() => setBrandsModalServer(null)}
            >
              <Text style={{ fontSize: 14, fontWeight: '600', color: '#666' }}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* ── Billing Schedule Modal ─────────────────────────────────────── */}
      <Modal
        visible={!!cycleModalServer}
        transparent
        animationType="fade"
        onRequestClose={() => setCycleModalServer(null)}
      >
        <TouchableOpacity
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}
          activeOpacity={1}
          onPress={() => setCycleModalServer(null)}
        >
          <TouchableOpacity activeOpacity={1} style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 380 }}>
            <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a2e', marginBottom: 4 }}>
              Billing Schedule
            </Text>
            <Text style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              Configure for <Text style={{ fontWeight: '700', color: '#0063ff' }}>{cycleModalServer}</Text>
            </Text>

            {/* ── Billing Mode ─── */}
            {CYCLE_OPTIONS.map((opt) => {
              const selected = cycleModalCurrent === opt;
              const labels: Record<string, string> = {
                daily: 'Daily — run at a set time (EST)',
                minutes: 'Every X Minutes',
                per_order: 'Per Order — charge immediately',
              };
              return (
                <TouchableOpacity
                  key={opt}
                  style={{
                    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                    paddingVertical: 12, paddingHorizontal: 14, borderRadius: 10, marginBottom: 6,
                    backgroundColor: selected ? '#E8F0FF' : '#f8f8f8',
                    borderWidth: selected ? 1.5 : 1,
                    borderColor: selected ? '#0063ff' : '#eee',
                  }}
                  onPress={() => setCycleModalCurrent(opt)}
                >
                  <Text style={{
                    fontSize: 14, fontWeight: selected ? '700' : '500',
                    color: selected ? '#0063ff' : '#333',
                  }}>
                    {labels[opt]}
                  </Text>
                  {selected && <FontAwesome5 name="check-circle" size={16} color="#0063ff" solid />}
                </TouchableOpacity>
              );
            })}

            {/* ── Daily: Time Picker (EST) ─── */}
            {cycleModalCurrent === 'daily' && (
              <View style={{ backgroundColor: '#f8f8f8', borderRadius: 10, padding: 14, marginTop: 10 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#555', marginBottom: 8 }}>
                  Run at (EST — 24hr format)
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Hour</Text>
                    <TextInput
                      style={{
                        borderWidth: 1, borderColor: '#ddd', borderRadius: 8,
                        paddingVertical: 8, paddingHorizontal: 12, fontSize: 16,
                        fontWeight: '700', textAlign: 'center', backgroundColor: '#fff',
                      }}
                      keyboardType="number-pad"
                      maxLength={2}
                      value={cycleModalHour}
                      onChangeText={(t) => {
                        const n = parseInt(t) || 0;
                        setCycleModalHour(n > 23 ? '23' : String(n));
                      }}
                    />
                  </View>
                  <Text style={{ fontSize: 24, fontWeight: '700', color: '#333', marginTop: 14 }}>:</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Minute</Text>
                    <TextInput
                      style={{
                        borderWidth: 1, borderColor: '#ddd', borderRadius: 8,
                        paddingVertical: 8, paddingHorizontal: 12, fontSize: 16,
                        fontWeight: '700', textAlign: 'center', backgroundColor: '#fff',
                      }}
                      keyboardType="number-pad"
                      maxLength={2}
                      value={cycleModalMinute}
                      onChangeText={(t) => {
                        const n = parseInt(t) || 0;
                        setCycleModalMinute(n > 59 ? '59' : String(n));
                      }}
                    />
                  </View>
                </View>
              </View>
            )}

            {/* ── Minutes: Interval Input ─── */}
            {cycleModalCurrent === 'minutes' && (
              <View style={{ backgroundColor: '#f8f8f8', borderRadius: 10, padding: 14, marginTop: 10 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#555', marginBottom: 8 }}>
                  Run every
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <TextInput
                    style={{
                      flex: 1, borderWidth: 1, borderColor: '#ddd', borderRadius: 8,
                      paddingVertical: 8, paddingHorizontal: 12, fontSize: 16,
                      fontWeight: '700', textAlign: 'center', backgroundColor: '#fff',
                    }}
                    keyboardType="number-pad"
                    maxLength={4}
                    value={cycleModalIntervalValue}
                    onChangeText={setCycleModalIntervalValue}
                  />
                  <Text style={{ fontSize: 14, fontWeight: '600', color: '#555' }}>
                    {cycleModalCurrent}
                  </Text>
                </View>
              </View>
            )}

            {/* ── Buttons ─── */}
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 20 }}>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: '#f0f0f0', alignItems: 'center' }}
                onPress={() => setCycleModalServer(null)}
              >
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#666' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{
                  flex: 1, paddingVertical: 12, borderRadius: 10,
                  backgroundColor: cycleModalSaving ? '#7aabff' : '#0063ff', alignItems: 'center',
                }}
                disabled={cycleModalSaving}
                onPress={() => {
                  if (!cycleModalServer) return;
                  setCycleModalSaving(true);
                  const payload: any = {
                    llc_billing_cycle: cycleModalCurrent,
                    llc_cron_interval_value: cycleModalIntervalValue,
                    llc_cron_hour: cycleModalHour,
                    llc_cron_minute: cycleModalMinute,
                  };
                  updateBillingSettings(cycleModalServer, payload, (res: any, status: number) => {
                    setCycleModalSaving(false);
                    if (status === 200) {
                      showAlert('success', `Billing schedule updated for ${cycleModalServer}`);
                      fetchAll();
                      setCycleModalServer(null);
                    } else {
                      showAlert('error', res?.error || 'Failed to update');
                    }
                  });
                }}
              >
                {cycleModalSaving ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>Update</Text>
                )}
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  container: { padding: 16, paddingBottom: 60 },
  card: {
    backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6, elevation: 3,
  },
  overviewCard: {
    backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
    borderLeftWidth: 4, borderLeftColor: '#0063ff',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4,
  },
  overviewHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: '#1a1a2e', marginBottom: 12 },
  serverTitle: { fontSize: 15, fontWeight: '700', color: '#0063ff', marginBottom: 8 },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  btnRow: { flexDirection: 'row', marginTop: 12 },
  label: { fontSize: 13, color: '#555', fontWeight: '500' },
  value: { fontSize: 13, color: '#222', fontWeight: '600' },
  valueHighlight: { fontSize: 16, color: '#0063ff', fontWeight: '700' },
  hint: { fontSize: 12, color: '#888', marginBottom: 12, lineHeight: 18 },
  disclaimer: { fontSize: 11, color: '#aaa', marginTop: 8, fontStyle: 'italic' },
  input: {
    borderWidth: 1, borderColor: '#ddd', borderRadius: 8,
    padding: 10, marginBottom: 10, color: '#222', backgroundColor: '#F8F9FA',
  },
  cardElementWrapper: {
    borderWidth: 1, borderColor: '#ddd', borderRadius: 8,
    padding: 12, marginBottom: 12, backgroundColor: '#F8F9FA',
  },
  btn: {
    borderRadius: 8, padding: 12, alignItems: 'center', justifyContent: 'center',
    flexDirection: 'row', marginTop: 8,
  },
  btnPrimary: { backgroundColor: '#0063ff' },
  btnSecondary: { backgroundColor: '#E8F0FF', borderWidth: 1, borderColor: '#0063ff' },
  btnSuccess: { backgroundColor: '#28A745' },
  btnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  warningBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#DC3545', borderRadius: 8, padding: 10, marginTop: 8,
  },
  warningText: { color: '#fff', fontSize: 12, fontWeight: '600' },
  cycleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  cycleBtn: { borderWidth: 1, borderColor: '#ddd', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6 },
  cycleBtnActive: { backgroundColor: '#0063ff', borderColor: '#0063ff' },
  cycleBtnText: { fontSize: 12, color: '#555' },
  cycleBtnTextActive: { color: '#fff', fontWeight: '700' },
  invoiceRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    borderBottomWidth: 1, borderBottomColor: '#f0f0f0', paddingVertical: 10,
  },
  invoiceDate: { fontSize: 12, color: '#555' },
  invoiceAmount: { fontSize: 14, fontWeight: '700', color: '#222' },
  invoiceError: { fontSize: 11, color: '#DC3545', marginTop: 2 },
  invoiceMeta: { fontSize: 11, color: '#888', marginTop: 2 },
  statusBadge: { fontSize: 11, fontWeight: '700', marginTop: 2 },
  orderRow: { borderBottomWidth: 1, borderBottomColor: '#eee', paddingVertical: 8 },
  orderNum: { fontSize: 13, fontWeight: '700', color: '#1a1a2e' },
  orderFee: { fontSize: 12, color: '#555', marginTop: 2 },
  emptyText: { color: '#aaa', textAlign: 'center', paddingVertical: 20 },
  tabBar: { flexDirection: 'row', marginBottom: 16, backgroundColor: '#f0f0f0', borderRadius: 10, padding: 4 },
  tab: { flex: 1, paddingVertical: 8, alignItems: 'center', borderRadius: 8 },
  tabActive: { backgroundColor: '#fff', shadowColor: '#000', shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  tabText: { fontSize: 13, color: '#888' },
  tabTextActive: { color: '#0063ff', fontWeight: '700' },
  backBtn: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  
  // Credit Card Styles
  creditCard: {
    borderRadius: 16,
    padding: 24,
    minHeight: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
    position: 'relative',
  },
  cardBrand: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  cardNumber: {
    fontSize: 22,
    fontWeight: '600',
    color: '#fff',
    letterSpacing: 3,
    fontFamily: 'monospace',
  },
  cardLabel: {
    fontSize: 9,
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '600',
    letterSpacing: 1,
    marginBottom: 4,
  },
  cardValue: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  cardChip: {
    position: 'absolute',
    top: 24,
    left: 24,
  },
});

export default PlatformBillingScreen;
